import { FunctionComponent, useEffect, useState } from "react";

import GroupIcon from "@/assets/icons/group.svg";
import NegativeIcon from "@/assets/icons/sentiment-negative.svg";
import PositiveIcon from "@/assets/icons/sentiment-positive.svg";
import UserIcon from "@/assets/icons/user.svg";
import { Badge } from "@/containers/InsightBadge";
import { cx } from "@/utils";

import styles from "./ImpactScore.module.css";

export type ImpactScoreProps = {
  className?: string;
} & WidgetProps<PackedWidgetData<ImpactScoreData>>;

import InfoLink from "@/containers/InfoLink";

import { BaseWidget } from "../../BaseWidget";
import { PackedWidgetData } from "../utils";
import { WidgetProps } from "../WidgetProps";
import ChannelPoint from "./partials/ChannelPoint";
import { POINT_RADIUS } from "./settings";
import { ImpactScoreChannel, ImpactScoreData, Point } from "./types";
import { placePoints } from "./utils";

const ImpactScoreInfoLink = () => (
  <InfoLink
    content={L.MENTIONS_IMPACT_SCORE_HOW_CALCULATED}
    href={L.HELP_ARTICLE_IMPACT_SCORE}
  />
);

const ImpactScore: React.FunctionComponent<{
  data: ImpactScoreData[];
  onChannelClick: (v: ImpactScoreChannel) => void;
}> = ({ data, onChannelClick }) => {
  const [channels, setChannels] = useState<Point[]>([]);

  useEffect(() => {
    if (data) {
      setChannels(placePoints(data));
    }
  }, [data]);

  return (
    <div className={styles.root}>
      {/** Offsetting the child with the radius of the items here makes sure they're never rendered offscreen */}
      <div className={styles.coordinates} style={{ margin: POINT_RADIUS }}>
        <span className={styles.x} />
        <span className={styles.y} />

        {channels.map((data, i) => (
          <ChannelPoint
            {...data}
            key={i}
            onClick={() => onChannelClick(data.channel)}
          />
        ))}
        {/** Axis labels */}
        <Badge
          className={styles.right}
          icon={PositiveIcon}
          style={{ marginRight: -POINT_RADIUS }}
          size="sm"
        >
          {L.REPORTS_WIDGET_IMPACT_SCORE_X_MAX_LABEL}
        </Badge>
        <Badge
          className={styles.left}
          icon={NegativeIcon}
          style={{ marginLeft: -POINT_RADIUS }}
          size="sm"
        >
          {L.REPORTS_WIDGET_IMPACT_SCORE_X_MIN_LABEL}
        </Badge>
        <Badge
          icon={GroupIcon}
          className={styles.top}
          style={{ marginTop: -POINT_RADIUS }}
          size="sm"
        >
          {L.REPORTS_WIDGET_IMPACT_SCORE_Y_MAX_LABEL}
        </Badge>
        <Badge
          icon={UserIcon}
          className={cx(styles.bottom)}
          style={{ marginBottom: -POINT_RADIUS }}
          size="sm"
        >
          {L.REPORTS_WIDGET_IMPACT_SCORE_Y_MIN_LABEL}
        </Badge>
      </div>
    </div>
  );
};

const ImpactScoreWidget: FunctionComponent<ImpactScoreProps> = (props) => {
  const hasData = (props?.data?.[0]?.results?.dataset || []).length > 0;
  return (
    <BaseWidget
      extraHeader={<ImpactScoreInfoLink />}
      {...props}
      hasData={hasData}
      title={L.REPORTS_WIDGET_DEFAULT_TITLE_IMPACT_SCORE_INSIGHTS}
      description={
        L.REPORTS_WIDGET_DEFAULT_TITLE_IMPACT_SCORE_INSIGHTS_SUBTITLE
      }
    >
      <ImpactScore
        {...props}
        data={props?.data?.[0]?.results?.dataset || []}
        onChannelClick={(channel) =>
          props.fetchMentions_v2({ channel_name: [channel.name] })
        }
      />
    </BaseWidget>
  );
};

export default ImpactScoreWidget;
